/**
 * Domain Knowledge Service
 *
 * Frontend service for domain knowledge management API.
 * Handles CRUD operations, gap analysis, research, and staleness tracking.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import { authFetch } from '@/utils/auth-fetch';

const BASE_URL = '/api/domain-knowledge';

export interface LegacyCleanupItem {
  id: string;
  title: string;
  branch: 'hallucination' | 'context' | 'missing_specification';
  status: string;
  researchQuestion: string;
  createdAt: string;
  usageCount: number | null;
  scopeProductId: string | null;
  scopeCategoryPath: string | null;
  kbeLinked: boolean;
  feedLinked: boolean;
  protectedReason: string | null;
  defaultSelected: boolean;
}

export interface LegacyCleanupPreview {
  filters: { siteId: string; branch: string; status: string };
  expiresAt: number;
  previewToken: string;
  matchBasis: string;
  items: LegacyCleanupItem[];
}

export interface LegacyCleanupResult {
  changedIds: string[];
  changedCount: number;
  previewCount: number;
  untouchedPreviewCount: number;
  usedCount: number;
}

async function legacyCleanupRequest<T>(action: 'preview' | 'apply', body: object): Promise<T> {
  const response = await authFetch(`${BASE_URL}/legacy-cleanup/${action}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || result.success !== true || !result.data) {
    throw new Error(result.error || `Legacy cleanup ${action} failed (HTTP ${response.status}).`);
  }
  return result.data;
}

export function previewLegacyCleanup(siteId: string, branch: string, status: string) {
  return legacyCleanupRequest<LegacyCleanupPreview>('preview', { siteId, branch, status });
}

export function applyLegacyCleanup(siteId: string, previewToken: string, selectedIds: string[], reason: string, confirmUsed: boolean) {
  return legacyCleanupRequest<LegacyCleanupResult>('apply', { siteId, previewToken, selectedIds, reason, confirmUsed });
}

// Types
export type KnowledgeType = 'terminology' | 'specification' | 'context' | 'expertise_signal' | 'validation_rule';
export type ScopeLevel = 'sector' | 'category' | 'brand' | 'attribute' | 'product';
export type KnowledgeStatus = 'proposed' | 'researching' | 'pending_review' | 'approved' | 'active' | 'deprecated' | 'resolved';
export type ResearchStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
export type ResearchMethodology = 'WEB_SEARCH' | 'MANUFACTURER_SPECS' | 'INDUSTRY_STANDARDS' | 'EXPERT_INPUT';

export interface DomainKnowledge {
  id: string;
  siteId: string;
  title: string;
  knowledgeText: string;
  knowledgeType: KnowledgeType;
  scopeLevel: ScopeLevel;
  scopeSectorId?: number;
  scopeCategoryPath?: string;
  scopeBrand?: string;
  scopeAttributeName?: string;
  scopeProductId?: string;
  status: KnowledgeStatus;
  researchStatus: ResearchStatus;
  researchQuestion?: string;
  researchMethodology?: ResearchMethodology;
  researchFindings?: string;
  researchSources?: string[];
  triggeredByFeedbackId?: string;
  gapAnalysis?: GapAnalysis;
  effectivenessScore?: number;
  createdBy?: string;
  creatorName?: string;
  approvedBy?: string;
  approverName?: string;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  // Category-scoped gap tracking
  affectedProductIds?: string[];
  affectedProductCount?: number;
}

export interface GapAnalysis {
  ROOT_CAUSE?: string;
  KNOWLEDGE_GAP?: string;
  CONFIDENCE?: string;
  SUGGESTED_CORRECTION?: string;
  SCOPE?: string;
  VERIFICATION_NEEDED?: string;
}

export interface KnowledgeFilters {
  siteId?: string;
  status?: KnowledgeStatus;
  knowledgeType?: KnowledgeType;
  scopeLevel?: ScopeLevel;
  researchStatus?: ResearchStatus;
  page?: number;
  limit?: number;
}

export interface StalenessRecord {
  id: string;
  content_type: string;
  content_id: string;
  domain_knowledge_id: string;
  staleness_reason: string;
  status: 'pending' | 'regenerating' | 'pending_validation' | 'validated' | 'dismissed';
  priority: number;
  validation_result?: 'improved' | 'no_change' | 'worse';
  knowledge_title?: string;
  knowledge_type?: KnowledgeType;
  scope_level?: ScopeLevel;
  created_at: string;
}

export interface StalenessFilters {
  siteId?: string;
  status?: string;
  contentType?: string;
  minPriority?: number;
  page?: number;
  limit?: number;
}

// Knowledge CRUD

export async function getKnowledgeList(filters: KnowledgeFilters = {}): Promise<{
  success: boolean;
  data?: { items: DomainKnowledge[]; total: number; page: number; totalPages: number };
  error?: string;
}> {
  const params = new URLSearchParams();

  if (filters.siteId) params.append('siteId', filters.siteId);
  if (filters.status) params.append('status', filters.status);
  if (filters.knowledgeType) params.append('knowledgeType', filters.knowledgeType);
  if (filters.scopeLevel) params.append('scopeLevel', filters.scopeLevel);
  if (filters.researchStatus) params.append('researchStatus', filters.researchStatus);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await authFetch(`${BASE_URL}?${params.toString()}`);
  return response.json();
}

export async function getKnowledge(id: string): Promise<{
  success: boolean;
  data?: DomainKnowledge;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/${id}`);
  return response.json();
}

export async function createKnowledge(data: Partial<DomainKnowledge>): Promise<{
  success: boolean;
  data?: DomainKnowledge;
  error?: string;
}> {
  const response = await authFetch(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateKnowledge(id: string, data: Partial<DomainKnowledge>): Promise<{
  success: boolean;
  data?: DomainKnowledge;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateKnowledgeStatus(id: string, status: KnowledgeStatus, options?: { reason?: string; notes?: string }): Promise<{
  success: boolean;
  data?: DomainKnowledge;
  error?: string;
}> {
  // Map status to the appropriate backend endpoint
  let endpoint: string;
  let body: Record<string, string> = {};

  switch (status) {
    case 'pending_review':
      endpoint = `${BASE_URL}/${id}/submit-for-review`;
      if (options?.notes) body.notes = options.notes;
      break;
    case 'approved':
      endpoint = `${BASE_URL}/${id}/approve`;
      break;
    case 'active':
      endpoint = `${BASE_URL}/${id}/activate`;
      break;
    case 'deprecated':
      endpoint = `${BASE_URL}/${id}/deprecate`;
      body.reason = options?.reason || 'Deprecated by user';
      break;
    default:
      // For other statuses, use PUT on the main endpoint
      const response = await authFetch(`${BASE_URL}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      return response.json();
  }

  const response = await authFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

export async function deleteKnowledge(id: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/${id}`, {
    method: 'DELETE',
  });
  return response.json();
}

// Gap Analysis

export async function analyzeGap(feedbackId: string): Promise<{
  success: boolean;
  feedbackId?: string;
  analysis?: GapAnalysis;
  proposal?: Partial<DomainKnowledge>;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/analyze/${feedbackId}`, {
    method: 'POST',
  });
  return response.json();
}

export async function analyzeAndCreate(feedbackId: string): Promise<{
  success: boolean;
  feedbackId?: string;
  analysis?: GapAnalysis;
  knowledge?: DomainKnowledge;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/analyze-and-create/${feedbackId}`, {
    method: 'POST',
  });
  return response.json();
}

// Research

export async function executeResearch(id: string): Promise<{
  success: boolean;
  knowledgeId?: string;
  content?: string;
  sources?: Array<{ url: string; title: string }>;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/research/${id}/execute`, {
    method: 'POST',
  });
  return response.json();
}

export async function submitManualResearch(id: string, data: {
  findings: string;
  sources?: Array<{ url: string; title: string }>;
  refinedKnowledge: string;
}): Promise<{
  success: boolean;
  data?: DomainKnowledge;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/research/${id}/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function retryResearch(id: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/research/${id}/retry`, {
    method: 'POST',
  });
  return response.json();
}

export async function getResearchQueue(siteId?: string): Promise<{
  success: boolean;
  data?: Array<{
    id: string;
    title: string;
    research_question: string;
    research_methodology: ResearchMethodology;
    knowledge_type: KnowledgeType;
    scope_level: ScopeLevel;
    created_at: string;
  }>;
  error?: string;
}> {
  const params = siteId ? `?siteId=${siteId}` : '';
  const response = await authFetch(`${BASE_URL}/research/queue${params}`);
  return response.json();
}

export async function getResearchStats(siteId?: string): Promise<{
  success: boolean;
  data?: {
    pending: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
  error?: string;
}> {
  const params = siteId ? `?siteId=${siteId}` : '';
  const response = await authFetch(`${BASE_URL}/research/stats${params}`);
  return response.json();
}

// Staleness

export async function propagateStaleness(knowledgeId: string): Promise<{
  success: boolean;
  data?: {
    knowledgeId: string;
    scopeLevel: ScopeLevel;
    affectedFound: number;
    affectedCount: number;
    priority: number;
  };
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/staleness/propagate/${knowledgeId}`, {
    method: 'POST',
  });
  return response.json();
}

export async function getStalenessQueue(filters: StalenessFilters = {}): Promise<{
  success: boolean;
  data?: {
    items: StalenessRecord[];
    total: number;
    page: number;
    totalPages: number;
  };
  error?: string;
}> {
  const params = new URLSearchParams();

  if (filters.siteId) params.append('siteId', filters.siteId);
  if (filters.status) params.append('status', filters.status);
  if (filters.contentType) params.append('contentType', filters.contentType);
  if (filters.minPriority) params.append('minPriority', filters.minPriority.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await authFetch(`${BASE_URL}/staleness?${params.toString()}`);
  return response.json();
}

export async function validateStaleness(stalenessId: string, data: {
  validationResult: 'improved' | 'no_change' | 'worse';
  notes?: string;
}): Promise<{
  success: boolean;
  data?: StalenessRecord;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/staleness/${stalenessId}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function dismissStaleness(stalenessId: string, reason?: string): Promise<{
  success: boolean;
  data?: StalenessRecord;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/staleness/${stalenessId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return response.json();
}

export async function getStalenessStats(siteId?: string): Promise<{
  success: boolean;
  data?: {
    pending: number;
    regenerating: number;
    pending_validation: number;
    validated: number;
    dismissed: number;
    avgPriority: Record<string, number>;
  };
  error?: string;
}> {
  const params = siteId ? `?siteId=${siteId}` : '';
  const response = await authFetch(`${BASE_URL}/staleness/stats${params}`);
  return response.json();
}

// Unified Knowledge Query

export async function queryUnifiedKnowledge(context: {
  siteId: string;
  sectorId?: number;
  categoryPath?: string;
  brand?: string;
  productTitle?: string;
  attributes?: Record<string, string>;
}): Promise<{
  success: boolean;
  items?: Array<{
    source: string;
    id: string;
    title: string;
    content: string;
    knowledge_type: string;
    scope_level: string;
    similarity: number;
    finalScore: number;
  }>;
  stats?: {
    totalFound: number;
    returned: number;
    bySource: Record<string, number>;
  };
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/unified/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(context),
  });
  return response.json();
}

export async function getKnowledgeStats(siteId?: string, sectorId?: number): Promise<{
  success: boolean;
  stats?: Record<string, { total: number; active: number }>;
  error?: string;
}> {
  const params = new URLSearchParams();
  if (siteId) params.append('siteId', siteId);
  if (sectorId) params.append('sectorId', sectorId.toString());

  const response = await authFetch(`${BASE_URL}/unified/stats?${params.toString()}`);
  return response.json();
}

// ============================================================================
// Story 48: Mass Regeneration
// ============================================================================

export interface RegenerationJob {
  jobId: string;
  knowledgeId: string;
  knowledgeTitle?: string;
  siteId: string;
  contentType: string;
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled';
  totalItems: number;
  completedItems: number;
  failedItems: number;
  progress: number;
  estimatedCost?: number;
  estimatedTime?: string;
  startedAt?: string;
  completedAt?: string;
  errorSummary?: unknown;
  createdAt: string;
}

export interface RegenerationEstimate {
  dryRun: true;
  knowledgeId: string;
  knowledgeTitle: string;
  scopeLevel: ScopeLevel;
  totalProducts: number;
  productsToRegenerate: number;
  estimatedCost: number;
  estimatedTime: string;
}

/**
 * Queue mass regeneration for products affected by a knowledge entry
 */
export async function queueRegeneration(
  knowledgeId: string,
  options: { dryRun?: boolean; contentType?: string } = {}
): Promise<{
  success: boolean;
  data?: RegenerationJob | RegenerationEstimate;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/${knowledgeId}/regenerate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });
  return response.json();
}

/**
 * Get count of products affected by a knowledge entry's scope
 */
export async function getAffectedProductsCount(knowledgeId: string): Promise<{
  success: boolean;
  data?: {
    totalProducts: number;
    withAeoContent: number;
    estimatedCost: number;
    scopeLevel: ScopeLevel;
    scopeValue: string;
  };
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/${knowledgeId}/affected-products`);
  return response.json();
}

/**
 * Get all regeneration jobs for a site
 */
export async function getRegenerationJobs(siteId: string, limit?: number): Promise<{
  success: boolean;
  data?: {
    jobs: RegenerationJob[];
    count: number;
  };
  error?: string;
}> {
  const params = new URLSearchParams({ siteId });
  if (limit) params.append('limit', limit.toString());

  const response = await authFetch(`${BASE_URL}/regeneration-jobs?${params.toString()}`);
  return response.json();
}

/**
 * Get regeneration job status
 */
export async function getRegenerationJobStatus(jobId: string): Promise<{
  success: boolean;
  data?: RegenerationJob;
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/regeneration-jobs/${jobId}`);
  return response.json();
}

/**
 * Cancel a regeneration job
 */
export async function cancelRegenerationJob(jobId: string): Promise<{
  success: boolean;
  data?: { jobId: string; status: 'cancelled' };
  error?: string;
}> {
  const response = await authFetch(`${BASE_URL}/regeneration-jobs/${jobId}/cancel`, {
    method: 'POST',
  });
  return response.json();
}
